import { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import { IntakeContext } from '../context/IntakeContext'
// import { Link } from "react-router-dom";
import { selectData } from 'layouts/onboarding'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFieldFocus } from 'context/FieldFocusContext'

// @mui material components
import HelpIcon from '@mui/icons-material/Help'
import { IconButton, Tooltip, Fade, Grid, Typography, Box } from '@mui/material'

import { UpdatePopup } from 'layouts/onboarding/UpdatePopup'

const StyledMSB = styled.div`
  text-align: center;
  position: relative;
  padding: 16px;
  height: 100%;
  cursor: pointer;
  border: ${(props) =>
    props.active ? '2px solid #a3a7aa' : '2px solid #e5e5e5'};
  background: ${(props) => (props.active ? '#eeeeee' : '#fff')};
  color: #212121;
  border-radius: 0.75rem;
  transition: 0.2s all ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    border: 2px solid #a3a7aa;
    background: #f5f5f5;
  }

  > h6,
  h5 {
    color: #212121;
    letter-spacing: 0.3px;
    font-weight: 600;
    line-height: 1.4;
    margin: 0;
  }

  > span {
    border-radius: 50%;
    height: 1.5em;
    width: 1.5em;
    padding: 8px;
    display: flex;
    justify-content: center;
    align-items: center;

    &.yesIcon {
      color: #4caf50;
      background-color: #dbefdc;
    }
    &.noIcon {
      color: #fd5c70;
      background-color: #fedee2;
    }
  }
`

const AgencyTileSelector = () => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const employmentStatusCheck =
    intakeState?.employmentStatus === 'retired' ? 'did' : 'do'

  const [agency, setAgency] = useState(intakeState.agency)

  const toggleAgency = (name) => {
    setAgency(name)
  }
  const [openFieldModal, setOpenFieldModal] = useState(false)
  const handleFieldModalClose = () => {
    setOpenFieldModal(false)
  }
  const handleFieldModalOpen = () => {
    setOpenFieldModal(true)
  }
  const excludedAgencies = [
    'u.s._postal_service_(usps)',
    'department_of_veterans_affairs_(va)',
    'department_of_defense_(dod)',
    'department_of_agriculture_(usda)',
    'u.s._department_of_state_(dos)',
    'defense_health_agency',
    '',
    null,
  ]

  useEffect(() => {
    if (typeof agency != 'string') return
    updateIntakeStateAtKey({
      agency: agency,
    })
  }, [agency])

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992)
  const fieldId = 'agencyHelp'
  const helpText =
    'Certain agencies have specific nuances to retirement rules. The agency you work for will change our calculations and recommendations for you.'

  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  useEffect(() => {
    registerHelpField(fieldId, helpText)
  }, [fieldId])

  const StyledAgencyLogo = styled.img`
    max-height: ${isMobile ? '30px' : '60px'};
    width: auto;
    max-width: 100%;
  `

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box
        mb={1}
        onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
        onMouseLeave={() => handleBlur()}
      >
        <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
          Which agency {employmentStatusCheck} you work for?
          {isMobile && (
            <Tooltip
              TransitionComponent={Fade}
              title="Certain agencies have specific nuances to retirement rules. The agency you work for will change our calculations and recommendations for you."
              placement="top"
              color="info"
              enterTouchDelay={0}
              leaveTouchDelay={3000}
            >
              <IconButton size="small" color="dark">
                <HelpIcon sx={{ marginTop: '-10px' }} />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        fontSize={{ xs: '16px', sm: '20px' }}
        onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
        onMouseLeave={() => handleBlur()}
      >
        Select one of the following:
      </Typography>
      <Box mt={2} mb={1}>
        <Grid
          container
          spacing={1}
          justifyContent="center"
          mb={3}
          onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
          onMouseLeave={() => handleBlur()}
        >
          <Grid item xs={12}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'agency',
              label: '🔎︎ Find Your Agency',
              hiddenLabel: true,
              inputType: 'select',
              value: intakeState.agency,
              searchable: true,
              enableHelpText: true,
              helpText:
                'Certain agencies have specific nuances to retirement rules. The agency you work for will change our calculations and recommendations for you.',
              onChange: (agency) => updateIntakeStateAtKey({ agency }),
              options: selectData.agency,
            })}
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={agency === 'u.s._postal_service_(usps)'}
              onClick={() => toggleAgency('u.s._postal_service_(usps)')}
            >
              <StyledAgencyLogo src="https://uspsblog.com/wp-content/uploads/2023/09/USPS_Eagle-Symbol-web-size-300x253-1.png" />
              <Typography variant="h6" textTransform="capitalize">
                Post Office
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={agency === 'department_of_veterans_affairs_(va)'}
              onClick={() =>
                toggleAgency('department_of_veterans_affairs_(va)')
              }
            >
              <StyledAgencyLogo src="https://www.va.gov/img/design/logo/va-og-twitter-image.png" />
              <Typography variant="h6" textTransform="capitalize">
                Veterans Affairs
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={agency === 'department_of_defense_(dod)'}
              onClick={() => toggleAgency('department_of_defense_(dod)')}
            >
              <StyledAgencyLogo src="https://upload.wikimedia.org/wikipedia/commons/2/2e/Seal_of_the_United_States_Department_of_Defense.svg" />
              <Typography variant="h6" textTransform="capitalize">
                Department of Defense
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={agency === 'department_of_agriculture_(usda)'}
              onClick={() => toggleAgency('department_of_agriculture_(usda)')}
            >
              <StyledAgencyLogo src="https://upload.wikimedia.org/wikipedia/commons/0/04/Logo_of_the_United_States_Department_of_Agriculture.svg" />
              <Typography variant="h6" textTransform="capitalize">
                USDA
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={agency === 'department_of_state_(dos)'}
              onClick={() => toggleAgency('department_of_state_(dos)')}
            >
              <StyledAgencyLogo src="https://upload.wikimedia.org/wikipedia/commons/7/7b/Seal_of_the_United_States_Department_of_State.svg" />
              <Typography variant="h6" textTransform="capitalize">
                State Department
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={agency === 'defense_health_agency'}
              onClick={() => toggleAgency('defense_health_agency')}
            >
              <StyledAgencyLogo src="https://upload.wikimedia.org/wikipedia/commons/0/00/Logo_of_Defense_Health_Agency.svg" />
              <Typography variant="h6" textTransform="capitalize">
                Defense Health Agency
              </Typography>
            </StyledMSB>
          </Grid>
          {/* <Grid item xs={6} sm={4}>
            <StyledMSB
              active={!excludedAgencies.includes(agency)}
              onClick={() => toggleAgency('other')}
            >
              <Icon fontSize="large" sx={{ marginTop: '20px' }}>
                <HelpIcon />
              </Icon>
              <Typography variant="h6" textTransform="capitalize">
                Other
              </Typography>
            </StyledMSB>
          </Grid> */}
        </Grid>
        {/* <Grid container> */}
        {/* <UpdatePopup
            handleClose={handleFieldModalClose}
            open={openFieldModal}
            handleSubmit={handleFieldModalClose}
            id="agency"
            label="Select Your Agency"
            placeholder="Agency"
            inputType="select"
            intakeAttribute={intakeState.agency}
            onChange={(agency) => {
              toggleAgency(agency)
              updateIntakeStateAtKey({ agency })
            }}
            options={selectData.agency}
          />
          {!excludedAgencies.includes(agency) ? (
            <>
              <Grid item xs={12} mb={2}>
                <Typography variant="body1">
                  Selected Agency:{' '}
                  {
                    selectData.agency.find(
                      ({ value }) => value === intakeState.agency
                    ).label
                  }
                </Typography>
              </Grid>
            </>
          ) : (
            <></>
          )} */}
        {/* {!excludedAgencies.includes(agency) ? (
            <>
              <Grid item xs={12} mb={2}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'agency',
                  label: 'Select Your Agency',
                  placeholder: '',
                  inputType: 'select',
                  value: intakeState.agency,
                  onChange: (agency) => updateIntakeStateAtKey({ agency }),
                  options: selectData.agency,
                })}
              </Grid>
            </>
          ) : (
            <></>
          )} */}
        {/* </Grid> */}
      </Box>
    </Box>
  )
}

export default AgencyTileSelector
